import React, { useState, useEffect, useCallback } from 'react';
import SettingsService from '../../services/SettingsService';

const AccountsSection = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('admin');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdPassword, setCreatedPassword] = useState(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const data = await SettingsService.listAdmins();
      setAdmins(data.admins);
      setLoading(false);
    } catch {
      setError('Failed to load admin accounts');
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) return;

    setCreating(true);
    setCreateError(null);
    setCreatedPassword(null);

    try {
      const data = await SettingsService.createAdmin({
        username: newUsername.trim(),
        email: newEmail.trim() || undefined,
        role: newRole,
      });
      setCreatedPassword(data.generatedPassword);
      setNewUsername('');
      setNewEmail('');
      setNewRole('admin');
      await fetchAdmins();
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await SettingsService.updateAdmin(admin.id, { active: !admin.active });
      await fetchAdmins();
    } catch {
      setError('Failed to update admin');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="settings-card">
        <div className="settings-loading"><i className="bi bi-arrow-repeat spin" /> Loading accounts...</div>
      </div>
    );
  }

  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <i className="bi bi-people" />
        Dashboard Accounts
      </div>

      {error && (
        <div className="data-error-banner" style={{ margin: '12px 16px' }}>
          <i className="bi bi-exclamation-triangle" />
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="settings-admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{admin.username}</td>
                <td>{admin.email || '—'}</td>
                <td style={{ textTransform: 'capitalize' }}>{admin.role}</td>
                <td>{formatDate(admin.lastLogin)}</td>
                <td>
                  <span className={`settings-status-badge settings-status-badge--${admin.active ? 'active' : 'inactive'}`}>
                    {admin.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button
                    className={`settings-toggle${admin.active ? ' active' : ''}`}
                    onClick={() => handleToggleActive(admin)}
                    aria-label={admin.active ? 'Deactivate' : 'Activate'}
                    style={{ transform: 'scale(0.85)' }}
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </td>
              </tr>
            ))}
            {admins.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>
                  No dashboard accounts found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form className="settings-create-form" onSubmit={handleCreate}>
        <div className="settings-form-group">
          <label className="settings-form-label">Username</label>
          <input
            type="text"
            className="settings-text-input"
            placeholder="username"
            value={newUsername}
            onChange={e => setNewUsername(e.target.value)}
            required
            style={{ width: '160px' }}
          />
        </div>
        <div className="settings-form-group">
          <label className="settings-form-label">Email</label>
          <input
            type="email"
            className="settings-text-input"
            placeholder="email@example.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            style={{ width: '200px' }}
          />
        </div>
        <div className="settings-form-group">
          <label className="settings-form-label" htmlFor="new-account-role">Role</label>
          <select
            id="new-account-role"
            className="settings-text-input"
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            style={{ width: '120px' }}
          >
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
          </select>
        </div>
        <button type="submit" className="settings-btn settings-btn--primary" disabled={creating || !newUsername.trim()}>
          {creating ? <><i className="bi bi-arrow-repeat spin" /> Creating...</> : <><i className="bi bi-plus-lg" /> Create</>}
        </button>
      </form>

      {createError && (
        <div className="settings-inline-error" style={{ padding: '0 20px 12px' }}>{createError}</div>
      )}

      {createdPassword && (
        <div style={{ padding: '8px 20px 16px' }}>
          <div className="settings-indicator settings-indicator--green" style={{ fontSize: '0.8rem' }}>
            <i className="bi bi-check-circle" />
            Account created. Generated password: <code style={{ fontWeight: 700 }}>{createdPassword}</code>
            <span style={{ fontSize: '0.7rem', opacity: 0.7 }}> (copy now — not shown again)</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsSection;
